import { useState } from 'react';
import gql from 'graphql-tag';
import { useMutation } from 'react-apollo';

import { BetSource, CompsBetSource } from '../../types';
import { usePrintCompVoucher } from './print';
import { CompVoucher, EntitlementDetails } from '.';
import { useDeviceInfo } from '../../retail-config/device';
import { trackEvent } from '../../fullstory';

type RedeemArgs = {
  voucher: CompVoucher;
  primaryIdentifier: string;
  entitlementDetails: EntitlementDetails;
};

export default function useRedeem() {
  const printCompVoucher = usePrintCompVoucher();
  const { features } = useDeviceInfo();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [redeemedVoucher, setRedeemedVoucher] = useState<CompVoucher | null>(null);

  const [redeemMutation] = useMutation<{ redeemCompVoucher: CompVoucher }, { voucherId: string }>(
    redeemCompVoucherMutation
  );

  return { redeem: redeemWithLoading, loading, error, redeemedVoucher };

  async function redeemWithLoading(args: RedeemArgs) {
    try {
      setLoading(true);
      setError(false);

      if (!redeemedVoucher) {
        const redeemedVoucher = await redeem(args.voucher.uuid);
        if (redeemedVoucher) {
          setRedeemedVoucher(redeemedVoucher);
        } else {
          return;
        }
      }

      await print(args);
    } finally {
      setLoading(false);
    }
  }

  async function redeem(uuid: string) {
    let redeemResult;
    try {
      redeemResult = await redeemMutation({ variables: { voucherId: uuid } });
    } catch (error) {
      setError(true);
      return;
    }

    if (!redeemResult.data || redeemResult.errors) {
      setError(true);
      return;
    }

    return redeemResult.data.redeemCompVoucher;
  }

  async function print({ entitlementDetails, voucher, primaryIdentifier }: RedeemArgs) {
    try {
      const rewardsCardNumber = primaryIdentifier;
      const source = voucher.source;

      const threshold = getThreshold({
        betSources: features.comps?.betSources,
        source
      });

      await printCompVoucher({
        ...entitlementDetails,
        uuid: voucher.uuid,
        rewardsCardNumber
      });

      trackEvent({
        name: 'comp-voucher-issued-v2',
        properties: {
          entitlementType: entitlementDetails.type,
          rewardsCardNumber: rewardsCardNumber,
          bettingSource: source,
          voucherThreshold: threshold ?? null
        }
      });

      setError(false);
    } catch (e) {
      setError(true);
    }
  }
}

function getThreshold({ betSources, source }: { betSources?: CompsBetSource; source: BetSource }): number | undefined {
  if (betSources && source in betSources) {
    return betSources[source].voucherThreshold / 100;
  }
  return undefined;
}

const redeemCompVoucherMutation = gql`
  mutation ($voucherId: String!) {
    redeemCompVoucher(voucherId: $voucherId) {
      entitlement
      status
      uuid
      redeemedAt
      source
    }
  }
`;

export const forTestsOnly = {
  redeemCompVoucherMutation
};
